import { css } from 'styled-components';
import { rgba } from 'polished';
import { SDS_FONT_WEIGHT_MEDIUM, SDS_FONT_FAMILY_PRIMARY, SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL, SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM, SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE, SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL, SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { SDS_MISC_ALPHA_MEDIUM, SDS_MISC_ALPHA_XSMALL, SDS_MISC_ALPHA_XXSMALL } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_SIZE_XXXXXXLARGE, SDS_SIZE_BORDER_RADIUS_NONE, SDS_SIZE_BORDER_RADIUS_XXXSMALL, SDS_SIZE_BORDER_RADIUS_XLARGE, SDS_SIZE_BORDER_RADIUS_XXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXXLARGE, SDS_SIZE_BORDER_RADIUS_XXXXXXLARGE, SDS_SIZE_BORDER_SMALL, SDS_SIZE_BORDER_LARGE } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_XXXXSMALL, SDS_SPACE_XXSMALL, SDS_SPACE_MEDIUM, SDS_SPACE_LARGE, SDS_SPACE_XSMALL, SDS_SPACE_SMALL, SDS_SPACE_XLARGE, SDS_SPACE_XXLARGE, SDS_SPACE_XXXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { bordered, scaledIcon } from '../common/util.js';
import { safariRoundedOutlinePolyfill } from '../common/polyfill.js';

const customColor = (item, { color, theme, variant }) => color &&
    variant &&
    theme.colors[`SDS_BRAND_BUTTON_COLOR_${item}_${variant}_${color}`.toUpperCase()];
// As CSS supports multiple backgrounds, but not multiple background-colors,
// the alpha overlay is implemented using linear-gradient function although
// it doesn't contain a visible gradient effect.
const alphaOverlay = (alphaLevel) => (props) => [textColor(props)]
    .map(color => rgba(color, alphaLevel))
    .map(color => `linear-gradient(${color}, ${color})`)
    .join('');
const backgroundColor = ({ alternative, color, theme, variant, }) => {
    var _a;
    // Removal of `alternative` is a BREAKING CHANGE as ABC, Prisma,
    // S-kaupat and Yhteishyvä currently have a customized color for it and
    // the change to new color/variant combination needs to be done manually.
    // Therefore `alternative` prop is supported if especially requested,
    // until the corresponding deprecated component color tokens are removed.
    return alternative
        ? theme.colors.SDS_BRAND_BUTTON_COLOR_BACKGROUND_ALTERNATIVE
        : (_a = customColor('BACKGROUND', { color, theme, variant })) !== null && _a !== void 0 ? _a : (variant === 'filled'
            ? color === 'primary'
                ? theme.colors.SDS_BRAND_COLOR_BACKGROUND_STRONG_PRIMARY
                : theme.colors.SDS_BRAND_COLOR_BACKGROUND_INVERSE_GREY
            : variant === 'tonal'
                ? color === 'primary'
                    ? theme.colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_PRIMARY
                    : theme.colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_GREY
                : 'transparent');
};
const borderColor = ({ color, theme, variant }) => {
    var _a;
    return (_a = customColor('BORDER', { color, theme, variant })) !== null && _a !== void 0 ? _a : (variant === 'outlined'
        ? color === 'primary'
            ? theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY
            : theme.colors.SDS_BRAND_COLOR_BORDER_DEFAULT_GREY
        : 'transparent');
};
const borderRadius = ({ rounding, sizing }) => rounding && rounding === 'sharp'
    ? SDS_SIZE_BORDER_RADIUS_NONE
    : rounding === 'small'
        ? SDS_SIZE_BORDER_RADIUS_XXXSMALL
        : sizing &&
            {
                // sizing: border-radius
                xsmall: SDS_SIZE_BORDER_RADIUS_XLARGE,
                small: SDS_SIZE_BORDER_RADIUS_XXXLARGE,
                medium: SDS_SIZE_BORDER_RADIUS_XXXXLARGE,
                large: SDS_SIZE_BORDER_RADIUS_XXXXXLARGE,
                xlarge: SDS_SIZE_BORDER_RADIUS_XXXXXXLARGE,
            }[sizing];
const borderWidth = ({ variant }) => variant === 'plain' ? 0 : SDS_SIZE_BORDER_SMALL;
const iconColor = ({ alternative, color, theme, variant, }) => {
    var _a;
    // Removal of `alternative` is a BREAKING CHANGE as ABC, Prisma,
    // S-kaupat and Yhteishyvä currently have a customized color for it and
    // the change to new color/variant combination needs to be done manually.
    // Therefore `alternative` prop is supported if especially requested,
    // until the corresponding deprecated component color tokens are removed.
    return alternative
        ? theme.colors.SDS_BRAND_BUTTON_COLOR_ICON_ALTERNATIVE
        : (_a = customColor('ICON', { color, theme, variant })) !== null && _a !== void 0 ? _a : (variant === 'filled'
            ? theme.colors.SDS_BRAND_COLOR_ELEMENT_INVERSE_GREY
            : // All other variants
                color === 'primary'
                    ? theme.colors.SDS_BRAND_COLOR_ELEMENT_STRONG_PRIMARY
                    : theme.colors.SDS_BRAND_COLOR_ELEMENT_DEFAULT_GREY);
};
const textColor = ({ alternative, color, theme, variant, }) => {
    var _a;
    // Removal of `alternative` is a BREAKING CHANGE as ABC, Prisma,
    // S-kaupat and Yhteishyvä currently have a customized color for it and
    // the change to new color/variant combination needs to be done manually.
    // Therefore `alternative` prop is supported if especially requested,
    // until the corresponding deprecated component color tokens are removed.
    return alternative
        ? theme.colors.SDS_BRAND_BUTTON_COLOR_TEXT_ALTERNATIVE
        : (_a = customColor('TEXT', { color, theme, variant })) !== null && _a !== void 0 ? _a : (variant === 'filled'
            ? theme.colors.SDS_BRAND_COLOR_TEXT_INVERSE_GREY
            : // All other variants
                color === 'primary'
                    ? theme.colors.SDS_BRAND_COLOR_TEXT_STRONG_PRIMARY
                    : theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY);
};
const fontMetrics = {
    // sizing: [font-size, line-height]
    xsmall: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
    small: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    medium: [SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE],
    large: [SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL],
    xlarge: [SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM],
};
const font = ({ sizing }) => sizing &&
    [fontMetrics[sizing]]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_MEDIUM} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const padding = ({ $compactPlain, $iconOnly, variant, sizing, }) => {
    const VERTICAL_INDEX = 0;
    const onlyVerticalPaddingForCompactPlain = (dimension, index) => (!$compactPlain || index === VERTICAL_INDEX ? dimension : '0');
    const smallerPaddingForIconOnly = (_, index) => $compactPlain || !$iconOnly || index === VERTICAL_INDEX;
    return (sizing &&
        {
            // size: [vertical, horizontal]
            // Exceptions:
            // - Icon only button uses the first value for all paddings
            // - Compact plain button has no horizontal padding
            xsmall: [SDS_SPACE_XXSMALL, SDS_SPACE_MEDIUM],
            small: [SDS_SPACE_XXSMALL, SDS_SPACE_LARGE],
            medium: [SDS_SPACE_XSMALL, SDS_SPACE_LARGE],
            large: [SDS_SPACE_SMALL, SDS_SPACE_XLARGE],
            xlarge: [SDS_SPACE_MEDIUM, SDS_SPACE_XXLARGE],
        }[sizing]
            .map(onlyVerticalPaddingForCompactPlain)
            .filter(smallerPaddingForIconOnly)
            .map(dimension => variant === 'plain' ? dimension : bordered(dimension))
            .join(' '));
};
// Defines the padding between the icon and other button content
const iconPadding = ({ $iconOnly, sizing }) => $iconOnly
    ? undefined
    : sizing === 'xsmall'
        ? SDS_SPACE_XXXSMALL
        : SDS_SPACE_XXSMALL;
function iconSize({ sizing }) {
    if (sizing) {
        return scaledIcon(fontMetrics[sizing]).iconSize;
    }
}
// A negative margin is set to compensate the whitespace within the icon asset.
// It ensures that icon doesn't exceed the button line-height.
//
// Exception: The icon in a compact plain icon only button doesn't have
// a negative horizontal margin as the button doesn't have horizontal padding.
const iconMargin = ({ $compactPlain, sizing }) => {
    if (sizing) {
        const { iconMargin } = scaledIcon(fontMetrics[sizing]);
        return $compactPlain ? `${iconMargin} 0` : iconMargin;
    }
};
const outlineColor = ({ theme }) => {
    var _a;
    return (_a = customColor('BORDER', { color: 'primary', theme, variant: 'outlined' })) !== null && _a !== void 0 ? _a : (typeof theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY === 'string'
        ? theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY
        : 'none');
};
const outlineOffset = SDS_SPACE_XXXXSMALL;
const outlineWidth = SDS_SIZE_BORDER_LARGE;
const ButtonStyle = css `
    align-items: center;
    background-color: ${backgroundColor};
    border: ${borderWidth} solid ${borderColor};
    border-radius: ${borderRadius};
    color: ${textColor};
    display: inline-flex;
    font: ${font};
    justify-content: center;
    min-width: ${({ $iconOnly }) => !$iconOnly && SDS_SIZE_XXXXXXLARGE};
    padding: ${padding};
    position: relative;
    text-align: center;

    /* [data-docs-state] selectors are only intended for component documentation purposes */
    &:hover,
    &:active,
    &[data-docs-state='hover'],
    &[data-docs-state='active'] {
        /* This is a semi-transparent layer on top of the background-color defined above */
        background-image: ${({ variant }) => alphaOverlay(variant === 'filled'
    ? SDS_MISC_ALPHA_XSMALL
    : SDS_MISC_ALPHA_XXSMALL)};
    }

    &:active,
    &:focus,
    &[data-docs-state='active'],
    &[data-docs-state='focus'] {
        box-shadow: 0 0 0 ${SDS_SPACE_XXXXSMALL}
            ${({ theme }) => theme.colors.SDS_BRAND_COLOR_BACKGROUND_DEFAULT_GREY};
        outline: ${outlineWidth} solid ${outlineColor};
        outline-offset: ${outlineOffset};
        ${props => safariRoundedOutlinePolyfill({
    borderRadius: borderRadius(props),
    borderWidth: borderWidth(props),
    outlineColor: outlineColor(props),
    outlineOffset,
    outlineWidth,
})};
    }

    &[aria-disabled='true'],
    &[disabled] {
        opacity: ${SDS_MISC_ALPHA_MEDIUM};
        pointer-events: none;
    }

    .button-icon {
        box-sizing: content-box;
        color: ${iconColor};
        height: ${iconSize};
        margin: ${iconMargin};
        padding-left: ${({ iconPos }) => iconPos === 'after' && iconPadding};
        padding-right: ${({ iconPos }) => iconPos === 'before' && iconPadding};
        width: ${iconSize};

        svg {
            height: auto;
            width: auto;
        }
    }
`;

export { ButtonStyle };
//# sourceMappingURL=Button.styles.js.map
